/**
 * Dialog feedback ownership.
 *
 * A modal is the user's whole world while it is open, so feedback for an
 * action taken inside one belongs *inside* it — not in a global banner pinned
 * to the bottom of the app, three layers away from where the user clicked.
 *
 * `StyledDialog` provides this context around its content. `useFeedbackZone`
 * consumes it: a `push` from a component rendered inside a dialog lands on
 * that dialog's inline band, and a push from anywhere else falls through to
 * the global banner exactly as before. Because `useApiMutation` and
 * `useAsyncAction` both already call `useFeedbackZone`, every existing call
 * site gets correct routing without being touched.
 *
 * The resolution point is the *component*, not the top-most open dialog, and
 * that is the behaviour we want. In the profile flow, `ContactMethodsEditor`
 * owns the delete mutation and sits inside `ProfileEditDialog` — not inside
 * the confirm it opens — so its error surfaces on the profile dialog even
 * though a confirm was the thing the user clicked. That is where the request
 * came from, so that is where the answer goes.
 *
 * For the separate, *visual* question of whether a dialog is stacked on top
 * of another one, see `dialogStack.ts` — tree position doesn't answer it.
 */
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useFeedbackStore, type FeedbackPushOptions, type FeedbackSeverity } from '@/store/useFeedbackStore';
import type { FeedbackLayerSink } from '@/api/feedbackLayerMeta';

export interface DialogLayerItem {
  id: string;
  message: string;
  severity: FeedbackSeverity;
}

export interface DialogLayerValue extends FeedbackLayerSink {
  /**
   * The third argument is either the opening the action ran in (a number,
   * #1174, sent by central routing) or the global store's `FeedbackPushOptions`
   * (Q-530 R8: accepted for parity with the global store and ignored: the band
   * sits on the white dialog). An options object means "this opening" unless
   * it carries `opening`. `source` (#1628) names the action: see `clearSource`;
   * a push forwarded to the global banner keeps it (#1628-c2).
   */
  push: (message: string, severity: FeedbackSeverity, openingOrOptions?: number | FeedbackPushOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const DialogLayerContext = createContext<DialogLayerValue | null>(null);
const DialogOpeningContext = createContext(0);

/** The nearest enclosing dialog's feedback sink, or null outside any dialog. */
export function useDialogLayer(): DialogLayerValue | null {
  return useContext(DialogLayerContext);
}

/**
 * The nearest enclosing dialog's current opening (#1174 I3a): a counter the
 * layer bumps on every closed → open transition. Read at render, it names the
 * opening the person is acting in; 0 outside any dialog.
 */
export function useDialogLayerOpening(): number {
  return useContext(DialogOpeningContext);
}

export function DialogLayerProvider({
  value,
  opening,
  children,
}: {
  value: DialogLayerValue;
  opening: number;
  children: ReactNode;
}) {
  return (
    <DialogLayerContext.Provider value={value}>
      <DialogOpeningContext.Provider value={opening}>{children}</DialogOpeningContext.Provider>
    </DialogLayerContext.Provider>
  );
}

/**
 * Severities that clear themselves. Success and info are acknowledgements —
 * they've done their job once read. Errors and warnings persist until the
 * user dismisses them or the dialog closes: the entire point of routing
 * feedback here was that errors were getting lost, and expiring them on a
 * timer while the user is still looking at the dialog re-introduces a
 * quieter version of the same bug. The dialog's own lifetime is the bound.
 */
const SELF_CLEARING: ReadonlySet<FeedbackSeverity> = new Set<FeedbackSeverity>(['success', 'info']);

/**
 * Severities a push to a layer that is not live forwards to the global banner
 * (#1174 I3c). A failure is never dropped; a "saved" acknowledgement for a
 * dialog the person already closed is not news, so it is.
 */
const FORWARDED_WHEN_NOT_LIVE: ReadonlySet<FeedbackSeverity> = new Set<FeedbackSeverity>(['error', 'warning']);

const AUTO_DISMISS_MS = 5000;

let nextId = 0;

interface TrackedItem {
  message: string;
  severity: FeedbackSeverity;
  /** Committed to the band while the dialog was open (#1174 I3b). */
  painted: boolean;
  /** The actions that raised it (#1628). Undefined = permanent: never cleared by source. */
  sources?: Set<string>;
}

/**
 * Owns one dialog's feedback items. Returns the items to render, the context
 * value to publish to descendants, and the dialog's current opening.
 *
 * The layer is LIVE while the dialog is open and mounted (#1174 I2/I3). A
 * push to a live layer lands in the band; any other push — the dialog closed
 * (still mounted with open=false), unmounted, or reopened since the action
 * ran (`opening` mismatch, I3a) — goes to the global banner for `error` and
 * `warning` and is dropped for `success` and `info` (I3c). This applies to
 * every pusher: central routing, `useApiMutation`, `useAsyncAction`, hand
 * pushes through `useFeedbackZone`.
 *
 * The open flag is written in a layout effect, so a promise-driven close
 * leaves no window between commit and a passive effect in which a push would
 * land in the band and then be wiped. The mounted flag is set in the effect
 * body and cleared in its cleanup, so StrictMode's double pass leaves it live.
 */
export function useDialogLayerFeedback(open: boolean): {
  items: DialogLayerItem[];
  dismiss: (id: string) => void;
  value: DialogLayerValue;
  opening: number;
} {
  const [items, setItems] = useState<DialogLayerItem[]>([]);

  // The opening counter is render state, derived during render (React's
  // "adjust state when a prop changes" pattern), so a child rendered in the
  // same pass as the closed → open transition already reads the new opening.
  const [openingState, setOpeningState] = useState(() => ({ open, opening: open ? 1 : 0 }));
  let opening = openingState.opening;
  if (openingState.open !== open) {
    opening = open ? openingState.opening + 1 : openingState.opening;
    setOpeningState({ open, opening });
  }

  const live = useRef({ open: false, mounted: false, opening });
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // What the band holds, kept synchronously beside `items` so the unmount
  // re-home sees a push that has not committed yet.
  const tracked = useRef(new Map<string, TrackedItem>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    tracked.current.delete(id);
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clearSource = useCallback(
    (source: string) => {
      for (const [id, entry] of [...tracked.current]) {
        if (entry.sources?.delete(source) && entry.sources.size === 0) dismiss(id);
      }
    },
    [dismiss],
  );

  const dismissAll = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current.clear();
    tracked.current.clear();
    setItems([]);
  }, []);

  const push = useCallback(
    (message: string, severity: FeedbackSeverity, openingOrOptions?: number | FeedbackPushOptions) => {
      const pushOpening = typeof openingOrOptions === 'number' ? openingOrOptions : openingOrOptions?.opening;
      const source = typeof openingOrOptions === 'object' ? openingOrOptions.source : undefined;
      const { open: isOpen, mounted, opening: current } = live.current;
      const sameOpening = pushOpening === undefined || pushOpening === current;
      if (!isOpen || !mounted || !sameOpening) {
        if (FORWARDED_WHEN_NOT_LIVE.has(severity)) {
          return useFeedbackStore.getState().push(message, severity, source === undefined ? undefined : { source });
        }
        nextId += 1;
        return `dlg-fb-dropped-${nextId}`;
      }

      nextId += 1;
      const id = `dlg-fb-${nextId}`;
      // Collapse an identical message rather than stacking duplicates, matching
      // the global banner's behaviour for a retrying request.
      // The merged item is permanent if either push had no source (#1628).
      let sources: Set<string> | undefined = source === undefined ? undefined : new Set([source]);
      for (const [otherId, other] of tracked.current) {
        if (other.message === message && other.severity === severity) {
          if (sources && other.sources) other.sources.forEach((x) => sources?.add(x));
          else sources = undefined;
          tracked.current.delete(otherId);
        }
      }
      // The same action settling again replaces its earlier items (#1628).
      if (source !== undefined) {
        for (const [otherId, other] of [...tracked.current]) {
          if (other.sources?.delete(source) && other.sources.size === 0) dismiss(otherId);
        }
        // The band now shows this source: a forwarded copy of it on the global banner would be a
        // second answer (#1628-d4, c2 obj 4). Keys are per row, so a sibling's failure stays.
        useFeedbackStore.getState().clearSource(source);
      }
      tracked.current.set(id, { message, severity, painted: false, ...(sources && { sources }) });
      setItems((prev) => [
        ...prev.filter((i) => !(i.message === message && i.severity === severity)),
        { id, message, severity },
      ]);

      if (SELF_CLEARING.has(severity)) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), AUTO_DISMISS_MS),
        );
      }
      return id;
    },
    [dismiss],
  );

  // Closing the dialog clears its band: the user is done with this surface,
  // and a stale error shouldn't be waiting for them the next time they open it.
  useLayoutEffect(() => {
    const wasOpen = live.current.open;
    live.current.open = open;
    live.current.opening = opening;
    if (wasOpen && !open) dismissAll();
  }, [open, opening, dismissAll]);

  // An item that reached the screen has been seen (#1174 I3b).
  useLayoutEffect(() => {
    for (const item of items) {
      const entry = tracked.current.get(item.id);
      if (entry) entry.painted = true;
    }
  }, [items]);

  // Mounted flag, and the re-home: if the layer is torn down while it still
  // holds messages that were pushed but never painted (a push and an
  // unmount in the same commit: "saved", then the dialog is removed), the
  // surface was yanked out from under them. Re-home those on the global
  // banner rather than dropping them; an item with exactly one source keeps
  // it (#1628-c2). Items already painted were shown while
  // the dialog was open; showing them again at the page bottom would show
  // the same answer twice (I3b).
  useLayoutEffect(() => {
    const state = live.current;
    const timerMap = timers.current;
    const trackedMap = tracked.current;
    state.mounted = true;
    return () => {
      state.mounted = false;
      timerMap.forEach(clearTimeout);
      timerMap.clear();
      const stranded = [...trackedMap.values()].filter((i) => !i.painted);
      trackedMap.clear();
      stranded.forEach((i) => {
        // One source keeps it, so a later success still clears it; zero or
        // several stay unsourced (the store has no multi-source push).
        const only = i.sources?.size === 1 ? [...i.sources][0] : undefined;
        useFeedbackStore.getState().push(i.message, i.severity, only === undefined ? undefined : { source: only });
      });
    };
  }, []);

  // Stable identity: push/dismiss/dismissAll are all reference-stable, so
  // descendants don't re-render every time this dialog re-renders.
  const value = useMemo<DialogLayerValue>(
    () => ({ push, dismiss, dismissAll, clearSource }),
    [push, dismiss, dismissAll, clearSource],
  );

  return { items, dismiss, value, opening };
}
